import { createSearchClientWithAds } from '@redteclab/retail-media-adserver-algolia-wrapper'
import type { SearchClient } from 'algoliasearch/lite'
import Cookies from 'cookies'

import {
  BLACKLISTED_SEARCH_TERMS,
  DEFAULT_FILTERS_LIST,
  PAGE_TYPE_CATEGORY,
} from 'shared/consts'
import { AD_SERVER_USER_ID_COOKIE } from 'shared/consts/adServer'
import { experimentCompeteraIsEnabledAndV1 } from 'shared/experiments/components/experimentCompetera'
import { isRetailMediaAdServerGlooEnvEnabled } from 'shared/experiments/utils/retailMedia'
import logger from 'shared/services/logger'
import {
  getSetAdUserIdentity,
  getTargetingKeywordsFromSearchParams,
  isAdServerEnabled,
  mapAdServerIdsToAcronymIds,
} from 'shared/utils/adServerUtils'
import { getCategoryPageDefaultSortingIndex } from 'shared/utils/algolia/filters'
import type { PageProperties } from 'types/pageProperties'
import type { PublicConfig } from 'types/shopConfig'
import { getTargetingContext } from 'views/utils/adServerUtils'

export const enhanceSearchClientWithAds = ({
  searchClient,
  pageProperties,
  publicConfig,
  cookies,
}: {
  searchClient: SearchClient
  pageProperties: PageProperties
  publicConfig: PublicConfig
  cookies: Cookies
}): SearchClient => {
  const { adServer, searchConfig } = publicConfig
  const {
    adServerSideContext,
    adsData,
    pageType,
    searchUrl,
    cmsContentCode,
    searchState: { query } = {},
    experiments,
  } = pageProperties
  const searchConfigToUse =
    experimentCompeteraIsEnabledAndV1(experiments) &&
    searchConfig.algoliaCompetera
      ? searchConfig.algoliaCompetera
      : searchConfig.algolia
  const { indexName, mpIndexName, topSellerCountIndex } = searchConfigToUse

  if (!isAdServerEnabled(experiments)) {
    return searchClient
  }

  const adServerKeywords = searchUrl
    ? getTargetingKeywordsFromSearchParams(searchUrl)
    : []

  /**
   * In Algolia, we use different indexes to retrieve sorted results like price ascending, top seller, etc.
   * We want to display ads only in specific search result indexes.
   * So ads should be shown when the following indexes are selected:
   */
  const defaultIndexNames: string[] = [indexName, mpIndexName]

  let defaultFilters: string | undefined = undefined

  if (pageType === PAGE_TYPE_CATEGORY) {
    const defaultIndexNamesWithSortingSuffix = defaultIndexNames.map(
      defaultIndexName => {
        return getCategoryPageDefaultSortingIndex(defaultIndexName)
      }
    )

    defaultIndexNames.push(
      topSellerCountIndex,
      ...defaultIndexNamesWithSortingSuffix
    )

    defaultFilters = `(${DEFAULT_FILTERS_LIST.join(' OR ')})`
  }

  const adServerContext = getTargetingContext({
    publicRuntimeConfig: {
      pageProperties,
      publicConfig,
    },
    adServerSideContext,
  })

  const adServerUser = mapAdServerIdsToAcronymIds({
    ...getSetAdUserIdentity(cookies),
    userId: cookies.get(AD_SERVER_USER_ID_COOKIE),
  })

  const { apiUrl: adServerApiUrl, glooApiUrl: adServerGlooApiUrl } = adServer
  const isGlooApiEnabled =
    isRetailMediaAdServerGlooEnvEnabled(experiments) &&
    Boolean(adServerGlooApiUrl)

  // @ts-expect-error: Custom SearchClient type differs from the algoliasearch SearchClient type.
  return createSearchClientWithAds(searchClient, {
    adServerApiUrl: isGlooApiEnabled ? adServerGlooApiUrl : adServerApiUrl,
    adServerContext: {
      ...(adServerContext || {}),
      searchTerm: query || cmsContentCode,
    },
    adServerKeywords,
    adServerUser,
    blacklistedSearchTerms: BLACKLISTED_SEARCH_TERMS,
    defaultIndexNames,
    onError: logger.error.bind(logger),
    adsData,
    defaultFilters,
    overwriteMasterProductWithVariant: true,
  })
}
